import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '../../api/apiContext';
import { queryKeys, useApiQuery } from '../../api/hooks';
import type { SupportGrantResponse } from '../../api/types';
import { useAuthStore } from '../../auth/authStore';
import { AsyncSection } from '../../components/AsyncSection';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { isReleased } from '../../config/release';

/**
 * The Laseera operator's side of support access (SEC-003, FR-110).
 *
 * Note what is not on this screen: any way to create a grant. An operator sees what they have been
 * given and can open a session against it, and that is the whole of their power here. The asymmetry
 * is the mechanism behind "Laseera cannot read your data unless you let us", so the screen states it
 * rather than leaving it to be inferred from the absence of a button.
 *
 * Opening a session swaps this browser's access token for a read-only one bound to that one clinic.
 * The clinic sees that it happened, in its own audit log, within the same request — which is why the
 * confirmation below says so before the operator commits, not after.
 */
export function PlatformSupportAccessPage() {
  const { t } = useTranslation();
  const client = useApiClient();
  const navigate = useNavigate();

  const openSupportSession = useAuthStore((state) => state.openSupportSession);
  const supportSession = useAuthStore((state) => state.supportSession);

  const [opening, setOpening] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const grants = useApiQuery<SupportGrantResponse[]>(
    queryKeys.platformSupportGrants(),
    '/platform/support/grants',
  );

  async function open(grant: SupportGrantResponse) {
    if (!grant.clinicId) {
      return;
    }
    setOpening(grant.id ?? '');
    setError(null);
    try {
      await openSupportSession(client, grant.clinicId, grant.clinicName ?? grant.clinicId);
      // Straight to the audit log: the first thing a support case usually needs, and the one screen
      // that shows the operator their own arrival already recorded. Home until the staged release
      // reaches the audit log, since an unreleased destination has no route.
      void navigate(isReleased('audit') ? '/audit' : '/');
    } catch (caught) {
      setError(caught);
    } finally {
      setOpening(null);
    }
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.platformSupport')}
        description={t('platformSupport.description')}
      />

      <div className="callout" style={{ marginBlockEnd: 'var(--space-5)' }}>
        <p className="state__title">{t('platformSupport.boundariesTitle')}</p>
        <ul style={{ marginBlock: 'var(--space-3)', paddingInlineStart: 'var(--space-5)' }}>
          <li>{t('platformSupport.boundaryGrantedByClinic')}</li>
          <li>{t('platformSupport.boundaryReadOnly')}</li>
          <li>{t('platformSupport.boundaryRecheck')}</li>
          <li>{t('platformSupport.boundaryAudited')}</li>
        </ul>
      </div>

      {error ? <ErrorState error={error} /> : null}

      <AsyncSection
        query={grants}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('platformSupport.emptyTitle')}
        emptyDescription={t('platformSupport.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('platformSupport.clinic')}</th>
                  <th scope="col">{t('support.reason')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('support.expires')}</th>
                  <th scope="col">{t('support.used')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const live = row.status === 'ACTIVE';
                  const inside = supportSession?.grantId === row.id;
                  return (
                    <tr key={row.id}>
                      <th scope="row">
                        {row.clinicName ?? <span className="ltr">{row.clinicId}</span>}
                      </th>
                      <td>{row.reason}</td>
                      <td>
                        <span className={statusBadge(row.status)}>
                          {t(`support.status.${row.status ?? 'EXPIRED'}`)}
                        </span>
                      </td>
                      <td className="numeric">{formatInstant(row.expiresAt)}</td>
                      <td>
                        {row.useCount ? (
                          <span title={formatInstant(row.lastUsedAt)}>
                            {t('support.usedTimes', { count: row.useCount })}
                          </span>
                        ) : (
                          <span className="badge badge--muted">{t('support.neverUsed')}</span>
                        )}
                      </td>
                      <td>
                        {inside ? (
                          <span className="badge badge--gold">{t('platformSupport.inside')}</span>
                        ) : live ? (
                          <button
                            type="button"
                            className="button button--ghost button--compact"
                            disabled={opening !== null}
                            onClick={() => void open(row)}
                          >
                            {opening === row.id
                              ? t('platformSupport.opening')
                              : t('platformSupport.open')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>
    </>
  );
}

function statusBadge(status: SupportGrantResponse['status']): string {
  if (status === 'ACTIVE') {
    return 'badge';
  }
  if (status === 'REVOKED') {
    return 'badge badge--danger';
  }
  return 'badge badge--muted';
}

/** Dates render in Western digits even in Arabic (ASM-004), so the raw ISO date is trimmed, not localised away. */
function formatInstant(value: string | undefined | null): string {
  if (!value) {
    return '—';
  }
  return value.slice(0, 16).replace('T', ' ');
}
